import {
  ArrowRight,
  CalendarDays,
  Clock3,
  Flame,
  Gamepad2,
  Medal,
  RotateCcw,
  Sparkles,
  Star,
  Trophy,
  Users,
  Zap,
  type LucideIcon,
} from "lucide-react";
import type { Game } from "../../data/games";
import { GameArtwork } from "../GameArtwork/GameArtwork";
import { SubpageHeader } from "../SubpageHeader/SubpageHeader";
import styles from "./GameHistoryView.module.css";

interface GameHistoryViewProps {
  games: readonly Game[];
  onBack: () => void;
  onBrowseGames: () => void;
  onMenuOpen: (trigger: HTMLButtonElement) => void;
  onSelectGame: (game: Game, trigger: HTMLButtonElement) => void;
}

interface HistoryEntry {
  gameId: string;
  when: string;
  duration: string;
  players: number;
  result: string;
  reward: string;
  icon: LucideIcon;
  tone: "violet" | "coral" | "mint";
}

const historyEntries: readonly HistoryEntry[] = [
  {
    gameId: "quiz-night",
    when: "Igår · 21:42",
    duration: "24 min",
    players: 7,
    result: "2:a plats",
    reward: "+45 XP",
    icon: Medal,
    tone: "violet",
  },
  {
    gameId: "who-in-the-room",
    when: "Fredag · 22:18",
    duration: "18 min",
    players: 6,
    result: "3 rundor",
    reward: "+30 XP",
    icon: Sparkles,
    tone: "mint",
  },
  {
    gameId: "never-have-i-ever",
    when: "12 juli · 20:56",
    duration: "31 min",
    players: 8,
    result: "Kvällens längsta",
    reward: "+40 XP",
    icon: Flame,
    tone: "coral",
  },
];

export function GameHistoryView({
  games,
  onBack,
  onBrowseGames,
  onMenuOpen,
  onSelectGame,
}: GameHistoryViewProps) {
  const entries = historyEntries.flatMap((entry) => {
    const game = games.find((candidate) => candidate.id === entry.gameId);
    return game ? [{ ...entry, game }] : [];
  });

  return (
    <>
      <SubpageHeader
        eyebrow="Din spelresa"
        title="Spelhistorik"
        onBack={onBack}
        onMenuOpen={onMenuOpen}
      />

      <main className={styles.view}>
        <section className={styles.hero} aria-labelledby="history-heading">
          <div className={styles.heroGrid} aria-hidden="true" />
          <div className={styles.heroTop}>
            <span className={styles.eyebrow}>
              <CalendarDays aria-hidden="true" />
              Senaste 30 dagarna
            </span>
            <span className={styles.streak}>
              <Flame aria-hidden="true" />
              3 kvällar
            </span>
          </div>
          <h1 id="history-heading">Bra spelat!</h1>
          <p>Gänget har samlat fart. Här är pulsen från era senaste kvällar.</p>

          <div className={styles.stats} aria-label="Sammanfattning">
            <div>
              <span className={styles.statIcon}>
                <Gamepad2 aria-hidden="true" />
              </span>
              <strong>3</strong>
              <small>Spel</small>
            </div>
            <div>
              <span className={styles.statIcon}>
                <Users aria-hidden="true" />
              </span>
              <strong>8</strong>
              <small>Spelare</small>
            </div>
            <div>
              <span className={styles.statIcon}>
                <Zap aria-hidden="true" />
              </span>
              <strong>115</strong>
              <small>XP</small>
            </div>
          </div>
        </section>

        <section className={styles.highlight} aria-labelledby="highlight-heading">
          <span className={styles.trophy}>
            <Trophy aria-hidden="true" />
          </span>
          <div>
            <span className={styles.eyebrow}>Månadens highlight</span>
            <h2 id="highlight-heading">Quizklättraren</h2>
            <p>Tre quizkvällar spelade. En till låser upp en ny badge.</p>
          </div>
          <div
            className={styles.badgeProgress}
            role="progressbar"
            aria-label="Progress mot badgen Quizklättraren"
            aria-valuemin={0}
            aria-valuemax={4}
            aria-valuenow={3}
          >
            <span />
          </div>
          <small>3 av 4</small>
        </section>

        <section className={styles.timeline} aria-labelledby="timeline-heading">
          <div className={styles.sectionHeading}>
            <div>
              <span className={styles.eyebrow}>Senaste rundorna</span>
              <h2 id="timeline-heading">Tillbaka i tiden</h2>
            </div>
            <span>{entries.length} spel</span>
          </div>

          <ol className={styles.entries}>
            {entries.map(({ game, icon: Icon, ...entry }) => (
              <li key={`${game.id}-${entry.when}`}>
                <span className={styles.timelineLine} aria-hidden="true" />
                <span className={styles.timelineDot} data-tone={entry.tone}>
                  <Icon aria-hidden="true" />
                </span>
                <article className={styles.historyCard}>
                  <div className={styles.artwork}>
                    <GameArtwork
                      imageUrl={game.imageUrl}
                      title={game.title}
                      fallbackLabel={game.fallbackLabel}
                      categoryId={game.categoryId}
                    />
                  </div>
                  <div className={styles.cardCopy}>
                    <span className={styles.when}>{entry.when}</span>
                    <h3>{game.title}</h3>
                    <div className={styles.details}>
                      <span>
                        <Clock3 aria-hidden="true" />
                        {entry.duration}
                      </span>
                      <span>
                        <Users aria-hidden="true" />
                        {entry.players}
                      </span>
                      <span>
                        <Star aria-hidden="true" />
                        {entry.result}
                      </span>
                    </div>
                    <div className={styles.cardFooter}>
                      <strong>{entry.reward}</strong>
                      <button
                        type="button"
                        aria-label={`Spela ${game.title} igen`}
                        onClick={(event) =>
                          onSelectGame(game, event.currentTarget)
                        }
                      >
                        <RotateCcw aria-hidden="true" />
                        Spela igen
                      </button>
                    </div>
                  </div>
                </article>
              </li>
            ))}
          </ol>
        </section>

        <button
          className={styles.browseButton}
          type="button"
          onClick={onBrowseGames}
        >
          Hitta nästa favorit
          <ArrowRight aria-hidden="true" />
        </button>
      </main>
    </>
  );
}
